/**
 * FindStore Component
 *
 * This component displays a list of stores with their names and addresses.
 * Users can select a store using radio buttons, which updates the
 * state to reflect the selected store. The selected store can then be
 * used for further actions, such as displaying additional information
 * or making a reservation.
 *
 * Features:
 * - Renders a scrollable list of stores on small screens.
 * - Utilizes radio buttons to allow users to select a store.
 * - Updates the UI to highlight the selected store.
 * - Displays an iframe (for tracking or analytics) on the right side.
 *
 * Store Data:
 * - The store information is defined in a static array with IDs, names,
 *   and addresses.
 *
 *
 **/
import React, { useEffect, useRef, useState } from 'react';
import { Store } from '../../../../../types/product/store';
import { usePathname, useSearchParams } from 'next/navigation';
import GoButton from 'public/icons/ag-icons/goButton.svg';
import DownIcon from 'public/icons/ag-icons/down.svg';

import { getFirstPlacePhotoUrl } from './NeighborhoodDiscovery';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { tablet, ultraTablet } from 'helpers/utils/screensizes';

import StoreBadges from './storeBadges';
import { getTodayTimings } from 'helpers/utils/storeTimings';
import { getWeekwiseTimingsFromWeekdayText } from 'helpers/utils/getWeekwiseTimingsFromWeekdayText';

// Haversine Formula to calculate the distance between two points (in miles)
export const calculateDistanceInMiles = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const R = 3958.8; // Radius of the Earth in miles
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // distance in miles
};

interface FindStoreProps {
  storeData: Store[];
  selectedPlace?: {
    name: string;
    location: {
      lat: number;
      lng: number;
    };
  };
  badges: any;
  amplienceStores?: any;
  handleSelectedPlace: (place: { id: string; name: string; location: { lat: number; lng: number } }) => void;
  userLocation?: {
    lat: number;
    lng: number;
  } | null;
  activeTab?: string;
  getStoreBadges: (store: any) => any[];
  isDesktopSize: boolean;
  isMobile: boolean;
  selectedLocale: string;
  getSVGElement?: any;
  clickAndCollect?: any;
  setNearByStores: any;
  query: any;
  setQuery: any;
  suggestions: any;
  setSuggestions: any;
  handleChange: any;
  getSVGElemen?: any;
  isDesktop?: boolean;
  nearbyStores?: any;
  isSmallMobile: any;
  currentLocationClicked?: any;
  citySearch: any;
  storeTimingsData?: any;
}

const FindStore = ({
  storeData,
  selectedPlace,
  handleSelectedPlace,
  userLocation,
  badges,
  amplienceStores,
  getStoreBadges,
  isDesktopSize,
  isMobile,
  selectedLocale,
  clickAndCollect,
  query,
  setQuery,
  suggestions,
  setSuggestions,
  handleChange,
  getSVGElement,
  isDesktop,
  setNearByStores,
  nearbyStores,
  isSmallMobile,
  currentLocationClicked,
  citySearch,
  storeTimingsData,
}: FindStoreProps) => {
  const [isClient, setIsClient] = useState(false);
  const [isopen, setIsopen] = useState<{ storeName: string | null }>({
    storeName: null,
  });
  const mapPin = getSVGElement('Map Pin');
  const alertIcon = getSVGElement('alert');
  const phoneIcon = getSVGElement('Phone');
  const emailIcon = getSVGElement('Mail');
  useEffect(() => {
    setIsClient(true);
  }, []);
  const [isTablet] = useMediaQuery(tablet);
  const [isUltraTablet] = useMediaQuery(ultraTablet);

  // Sort stores alphabetically by name (A to Z)
  const sortedStoreData = [...storeData]?.sort((a, b) => {
    if (!a?.name || !b?.name) return 0;
    return a?.name?.trim().localeCompare(b?.name?.trim());
  });

  // useEffect(() => {
  //   setNearByStores(nearbyStores);
  // }, [selectedPlace]);

  // const getStoreBadges = (store: any) => {
  //   if (!amplienceStores || !badges) return [];
  //   // Find the amplience store that matches the current store by ID/storeKey
  //   const amplienceStore = Array.isArray(amplienceStores)
  //     ? amplienceStores.find((s: any) => s.storeKey === store.ID)
  //     : null;
  //   if (!amplienceStore || !amplienceStore.storeBadges) return [];
  //   // Map badge IDs to badge objects
  //   const sortedBadges = amplienceStore.storeBadges
  //     .map((badgeId: string) => badges.find((badge: any) => badge.id === badgeId))
  //     .filter(Boolean);
  //   return sortedBadges;
  // };

  // filter store by query (selectedPlace)
  const queryStore = selectedPlace
    ? sortedStoreData?.find(
        (store) => store?.name?.trim()?.toLowerCase() === selectedPlace?.name?.trim()?.toLowerCase(),
      )
    : null;

  // Decide what to render based on the 4 cases
  let finalStores: Store[] = [];

  if (!selectedPlace && !userLocation && !citySearch) {
    // Case 1: no query + location off → all stores
    finalStores = sortedStoreData;
  } else if (!selectedPlace && !userLocation && citySearch) {
    finalStores = sortedStoreData;
  } else if (!selectedPlace && userLocation && citySearch) {
    finalStores = storeData;
  } else if (selectedPlace && userLocation) {
    // const filterQueryStore = sortedStoreData?.filter(
    //   (i: any) => i?.name?.trim()?.toLowerCase() != queryStore?.name?.trim()?.toLowerCase(),
    // );

    // const nearbySorted = nearbyStores
    //   ?.filter((s: any) => s?.latitude && s?.longitude)
    //   ?.map((s: any) => ({
    //     ...s,
    //     distance: calculateDistanceInMiles(userLocation.lat, userLocation.lng, s.latitude, s.longitude),
    //   }))
    //   ?.sort((a: any, b: any) => a.distance - b.distance);

    finalStores = queryStore
      ? [queryStore, ...nearbyStores?.filter((s: any) => s?.ID !== queryStore?.ID)]
      : nearbyStores;
  } else if (!selectedPlace && userLocation && !currentLocationClicked) {
    // Case 3: no query + location on → all nearby stores sorted by distance
    finalStores = sortedStoreData;
    // ?.filter((s: any) => s?.latitude && s?.longitude)
    // ?.map((s: any) => ({
    //   ...s,
    //   distance: calculateDistanceInMiles(userLocation.lat, userLocation.lng, s.latitude, s.longitude),
    // }))
    // ?.sort((a: any, b: any) => a.distance - b.distance);
  } else if (currentLocationClicked && userLocation && !selectedPlace) {
    // Calculate nearby stores based on userLocation and sort by distance (near to far)
    finalStores = sortedStoreData
      ?.filter((s: any) => s?.latitude && s?.longitude)
      ?.map((s: any) => ({
        ...s,
        distance: calculateDistanceInMiles(userLocation.lat, userLocation.lng, s.latitude, s.longitude),
      }))
      ?.sort((a: any, b: any) => a.distance - b.distance);
  } else if (selectedPlace && !userLocation) {
    // Case 4: query + location off → query store first + alphabetical others
    const filterQueryStore = sortedStoreData?.filter(
      (i: any) => i?.name?.trim()?.toLowerCase() !== queryStore?.name?.trim()?.toLowerCase(),
    );
    finalStores = filterQueryStore && queryStore ? [queryStore, ...filterQueryStore] : [];
  }

  return (
    <>
      {isClient && finalStores?.length > 0 ? (
        <div>
          {finalStores?.map((store: any, index: number) => {
            const baseLocation = selectedPlace?.location || userLocation;

            const distance =
              baseLocation?.lat && baseLocation?.lng && store?.latitude && store?.longitude
                ? calculateDistanceInMiles(baseLocation?.lat, baseLocation?.lng, store?.latitude, store?.longitude)
                : null;

            const mapAPIKey = process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY;

            const storeBadges = getStoreBadges(store);
            const isSelected = selectedPlace?.name?.trim() === store?.name?.trim();
            //Amplience Stores timings are mapped here
            // const timings = getTodayTimings(store);
            //Amplience Stores timings are mapped here
            // const weekwiseTimings = getWeekwiseTimingsFromWeekdayText(store);
            const amplienceStore =
              Array.isArray(amplienceStores) && store
                ? amplienceStores?.find((s: any) => s?.storeKey === store?.ID)
                : null;
            const csvStoreTiming = storeTimingsData?.[store?.ID]?.timings ?? [];
            const timings = getTodayTimings(csvStoreTiming);
            const weekwiseTimings = getWeekwiseTimingsFromWeekdayText(csvStoreTiming);
            const g = amplienceStore?.imageActivationForAmplience ?? false;
            const googlePhotoUrl = getFirstPlacePhotoUrl(store, mapAPIKey);
            const ampliencePhotoUrl = amplienceStore?.storeImage
              ? `https://americangolf.a.bigcontent.io/v1/static/${amplienceStore?.storeImage?.name}`
              : null;
            const storeImageUrl = g ? ampliencePhotoUrl : '';
            const alertMessages = amplienceStores?.find((s: any) => s?.storeKey === store?.ID);
            const storeAlerts = alertMessages?.alerts || [];
            return (
              <div
                className={`flex items-center border-b px-12 py-16 transition-all duration-200 hover:border-2 hover:border-[#272B5C]
    md:px-16 md:py-12
    ${isSelected ? 'border-2 border-[#272B5C]' : ''}`}
                key={index}
                onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                  e.stopPropagation();
                  handleSelectedPlace({
                    id: store?.ID,
                    name: store?.name,
                    location: {
                      lat: store?.latitude,
                      lng: store?.longitude,
                    },
                  });
                  if (!isTablet) {
                    setIsopen((prev) => ({
                      storeName: prev?.storeName === store?.name ? null : store?.name, // toggle
                    }));
                  }
                }}
              >
                <div className="flex flex-1 flex-col items-start justify-start gap-y-24">
                  <div className="flex w-full flex-col gap-y-16">
                    <div className="flex items-start gap-x-8">
                      <div
                        key={`store-${store?.ID}`}
                        className="mt-2 flex w-full cursor-pointer flex-col gap-y-16 rounded"
                      >
                        <div className="flex cursor-pointer items-center justify-between">
                          <p className="font-body text-16 font-bold uppercase leading-[100%]">{store?.name}</p>
                          <div className="flex flex-shrink-0 items-center gap-x-8">
                            {userLocation && distance && selectedPlace?.name !== store.name ? (
                              <p className="ml-4 font-body text-16 font-bold lowercase leading-[100%]">
                                {distance?.toFixed(1)} mi
                              </p>
                            ) : (
                              <p className="ml-4 font-body text-16 font-bold lowercase leading-[100%]">
                                {userLocation &&
                                  calculateDistanceInMiles(
                                    userLocation.lat,
                                    userLocation.lng,
                                    store.latitude,
                                    store.longitude,
                                  ).toFixed(1) + ' mi'}{' '}
                              </p>
                            )}
                            <GoButton className={` cursor-pointer ${isTablet ? 'flex' : 'hidden'}`} />
                            <div
                            // onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                            //   e.stopPropagation();
                            //   setIsopen((prev) => ({
                            //     storeName: prev?.storeName === store?.name ? null : store?.name, // toggle
                            //   }));
                            // }}
                            >
                              <DownIcon
                                className={`flex transform cursor-pointer transition-transform duration-300 ${
                                  isTablet && 'hidden'
                                } ${isopen?.storeName === store?.name ? 'rotate-180' : 'rotate-0'}`}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-y-8">
                          {store?.address1 &&
                          store?.address2 &&
                          store?.city &&
                          store?.postalCode &&
                          store?.stateCode &&
                          store?.countryCode ? (
                            <div className="">
                              <p className="font-body text-16 font-normal leading-[130%] text-[#757575]">
                                {store?.address1}, {store?.address2}
                              </p>
                              <p className="font-body text-16 font-normal leading-[130%] text-[#757575]">
                                {store?.city}, {store?.postalCode}, {store?.stateCode}, {store?.countryCode}
                              </p>
                            </div>
                          ) : (
                            <div className="">
                              <p className="font-body text-16 font-normal leading-[130%] text-[#757575]">
                                {storeTimingsData?.[store?.ID]?.address}
                              </p>
                            </div>
                          )}

                          <div className="opening-closing-timings flex items-center gap-x-4">
                            <p className="font-body text-[16px] font-bold leading-[130%] text-[#1E1E1E]">
                              {timings ? (timings?.open_now ? 'Open' : 'Closed') : 'Timings'}
                            </p>
                            <span
                              className={`font-body ${isTablet && !isDesktop && 'font-normal'} text-16 ${
                                isDesktop && 'font-bold'
                              } font-bold leading-[130%] text-[#2C2C2C]`}
                            >
                              ·
                            </span>
                            <p className="font-body text-16 font-normal leading-[130%] text-[#2C2C2C]">
                              {timings
                                ? timings?.open_now
                                  ? `Closes at ${timings?.closeTime}`
                                  : `Opens at ${timings?.openTime}`
                                : 'Not available'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    {storeAlerts?.length > 0 && (
                      <div className="alerts flex flex-col gap-y-16">
                        {storeAlerts?.map((alert: any, index: any) =>
                          alert?.checkbox ? (
                            <div
                              key={index}
                              className="flex items-center gap-12 rounded-md border border-[#EC221F] bg-[#FEE9E7] p-12"
                            >
                              <div className="flex flex-1 shrink-0 basis-0 items-start gap-x-12">
                                {alertIcon?.iconElement && alertIcon?.svgUrl && (
                                  <img
                                    src={alertIcon?.svgUrl}
                                    alt={alertIcon?.iconElement?.iconName || 'Alert Icon'}
                                    className=""
                                    style={{
                                      width: isDesktopSize
                                        ? `${alertIcon?.iconElement?.iconWidthDesktopPx}px`
                                        : `${alertIcon?.iconElement?.iconWidthMobilePx}px`,
                                      height: isDesktopSize
                                        ? `${alertIcon?.iconElement?.iconHeightDesktopPx}px`
                                        : `${alertIcon?.iconElement?.iconHeightMobilePx}px`,
                                    }}
                                  />
                                )}
                                <div className="flex flex-col gap-y-4">
                                  <p className="font-body text-16 font-bold leading-[130%] text-[#900B09]">Alert</p>
                                  <p className="font-body text-14 font-normal leading-[130%] text-[#900B09]">
                                    {alert?.alertMessage}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : null,
                        )}
                      </div>
                    )}
                    {timings?.open_now && (
                      <div className="flex items-start gap-x-8">
                        <div className="pt-3">
                          {clickAndCollect?.iconElement && clickAndCollect?.svgUrl && (
                            <img
                              src={clickAndCollect?.svgUrl}
                              alt={clickAndCollect?.iconElement?.iconName || 'Search Icon'}
                              className=""
                              style={{
                                width: isDesktopSize
                                  ? `${clickAndCollect?.iconElement?.iconWidthDesktopPx}px`
                                  : `${clickAndCollect?.iconElement?.iconWidthMobilePx}px`,
                                height: isDesktopSize
                                  ? `${clickAndCollect?.iconElement?.iconHeightDesktopPx}px`
                                  : `${clickAndCollect?.iconElement?.iconHeightMobilePx}px`,
                              }}
                            />
                          )}
                        </div>
                        <div className="flex flex-col gap-y-8">
                          <p className="font-body text-16 font-bold leading-[100%] text-[#1E1E1E]">Click + Collect</p>
                          <p className="font-body text-16 font-normal leading-[100%] text-[#1E1E1E]">
                            Available from this store
                          </p>
                        </div>
                      </div>
                    )}
                    {storeImageUrl && isopen?.storeName === store?.name && (
                      <div className="flex w-full items-stretch">
                        <img
                          className=" aspect-[16/9] h-full w-full  items-stretch object-cover"
                          src={storeImageUrl}
                          alt={amplienceStore?.storeImage?.name || store?.name || 'Store Image'}
                        />
                      </div>
                    )}
                  </div>
                  {storeBadges?.length > 0 && (
                    <div className="bagdes flex w-fit flex-col gap-y-8">
                      <div className="badges flex w-fit flex-col gap-y-8">
                        <StoreBadges
                          badges={storeBadges}
                          isUltraLargeTablet={isTablet}
                          isMobile={isMobile}
                          selectedLocale={selectedLocale}
                        />
                      </div>
                    </div>
                  )}
                  {/* <div className="flex w-full flex-col gap-y-16"> */}
                  {isopen?.storeName === store?.name && (
                    <div className="flex w-full flex-col items-start gap-y-16">
                      <p className="font-body text-16 font-bold uppercase leading-[100%]">Hours</p>
                      <div className="weekwise w-full gap-2">
                        {weekwiseTimings?.map(({ day, time, index }: any) => (
                          <div key={index} className="flex justify-between ">
                            <span className="font-body text-16 font-normal leading-[130%] text-[#757575]">{day}</span>
                            <div className="flex gap-x-5">
                              <span className="font-body text-16 font-normal leading-[130%] text-[#757575]">
                                {time?.openTime}
                              </span>
                              <span className="font-body text-16 font-normal leading-[130%] text-[#757575]">-</span>
                              <span className="font-body text-16 font-normal leading-[130%] text-[#757575]">
                                {time.closeTime}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* </div> */}
                  {isopen?.storeName === store?.name && (
                    <div className="flex flex-col items-start gap-y-16">
                      <p className="font-body text-16 font-bold uppercase leading-[100%]">{'Contact Us'}</p>
                      <div className="flex flex-col gap-y-4">
                        <div className="flex items-center gap-x-8">
                          {phoneIcon?.iconElement && phoneIcon?.svgUrl && (
                            <img
                              src={phoneIcon?.svgUrl}
                              alt={phoneIcon?.iconElement?.iconName || 'Phone Icon'}
                              style={{
                                width: isDesktopSize
                                  ? `${phoneIcon?.iconElement?.iconWidthDesktopPx}px`
                                  : `${phoneIcon?.iconElement?.iconWidthMobilePx}px`,
                                height: isDesktopSize
                                  ? `${phoneIcon?.iconElement?.iconHeightDesktopPx}px`
                                  : `${phoneIcon?.iconElement?.iconHeightMobilePx}px`,
                              }}
                            />
                          )}
                          <p className="font-body text-16 font-normal leading-[130%] underline">
                            {store?.phone
                              ? store?.phone
                              : storeTimingsData?.[store?.ID]?.phone
                              ? storeTimingsData?.[store?.ID]?.phone
                              : 'Not available'}
                          </p>
                        </div>
                        <div className="flex items-center gap-x-8">
                          {emailIcon?.iconElement && emailIcon?.svgUrl && (
                            <img
                              src={emailIcon?.svgUrl}
                              alt={emailIcon?.iconElement?.iconName || 'Email Icon'}
                              style={{
                                width: isDesktopSize
                                  ? `${emailIcon?.iconElement?.iconWidthDesktopPx}px`
                                  : `${emailIcon?.iconElement?.iconWidthMobilePx}px`,
                                height: isDesktopSize
                                  ? `${emailIcon?.iconElement?.iconHeightDesktopPx}px`
                                  : `${emailIcon?.iconElement?.iconHeightMobilePx}px`,
                              }}
                            />
                          )}
                          <p className="font-body text-16 font-normal leading-[130%] underline">
                            {store?.email
                              ? store?.email
                              : storeTimingsData?.[store?.ID]?.email
                              ? storeTimingsData?.[store?.ID]?.email
                              : 'Not available'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    className="group w-fit cursor-pointer items-center justify-center gap-8 rounded-sm border  bg-[#2C2C2C] px-12 py-16 hover:bg-[#1e1e1e]"
                    onClick={() => {
                      if (userLocation && store?.latitude && store?.longitude) {
                        const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLocation?.lat},${userLocation?.lng}&destination=${store?.latitude},${store?.longitude}&travelmode=driving`;
                        window.open(mapsUrl, '_blank');
                      } else if (store?.latitude && store?.longitude) {
                        // Fallback: no userLocation, only set destination
                        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${store?.latitude},${store?.longitude}&travelmode=driving`;
                        window.open(mapsUrl, '_blank');
                      } else {
                        alert('Store location not available');
                      }
                    }}
                  >
                    <p className="font-body text-16 font-bold leading-[100%]  text-white group-hover:text-white">
                      Get Directions
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="ml-10 mt-40 w-[70%]">We are sorry we could not find results for your search</div>
      )}
    </>
  );
};

export default FindStore;
